import {useState, useEffect, useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {DropResult} from 'react-beautiful-dnd';
import {ProjectIndex} from 'types/project';
import arrayReorder from 'utils/array-reorder';
import toast from 'store/ui/actions/toast';
import {useGetProjectsQuery} from 'features/studio/projects/store/get-projects';
import {
  useReorderProjectsMutation,
  ProjectReorderItem,
  ProjectReorderPreviewItem,
} from 'features/studio/projects/store/reorder-projects';
import {getProjectCategory} from './project-category';

const useProjectsReorder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {data: projects, isLoading: loading} = useGetProjectsQuery({
    limit: 500,
    is_online: true,
  });
  const [items, setItems] = useState<ProjectIndex[]>([]);
  const [previewItems, setPreviewItems] = useState<
    ProjectReorderPreviewItem[] | null
  >(null);
  const [typeFilter, setTypeFilter] = useState<string | undefined>('interiors');
  const [reorderProjects, {isLoading: saving}] = useReorderProjectsMutation();

  useEffect(() => {
    if (projects?.data) {
      setItems(projects.data as unknown as ProjectIndex[]);
    }
  }, [projects]);

  const matchFilter = useCallback(
    (item: ProjectIndex): boolean =>
      !typeFilter || getProjectCategory(item.project_type) === typeFilter,
    [typeFilter]
  );

  /**
   * Drag end. We reorder only inside the currently visible (filtered) subset
   * — the user filters by category and drags within it, so non-matching
   * items must stay where they are. We map the filtered indexes back to the
   * full `items` array.
   */
  const onDragEnd = useCallback(
    (result: DropResult) => {
      const {destination, source} = result;
      if (!destination) return;
      if (destination.index === source.index) return;

      setItems(prev => {
        const filtered = prev.filter(matchFilter);
        const reorderedFiltered = arrayReorder(
          filtered,
          source.index,
          destination.index
        );
        let fi = 0;
        return prev.map(it =>
          matchFilter(it) ? reorderedFiltered[fi++] : it
        );
      });
      setPreviewItems(null);
    },
    [matchFilter]
  );

  const moveToFirst = useCallback(
    (itemId: string) => {
      setItems(prev => {
        const filtered = prev.filter(matchFilter);
        const idx = filtered.findIndex(it => String(it.id) === itemId);
        if (idx <= 0) return prev;
        const reordered = arrayReorder(filtered, idx, 0);
        let fi = 0;
        return prev.map(it => (matchFilter(it) ? reordered[fi++] : it));
      });
      setPreviewItems(null);
    },
    [matchFilter]
  );

  const moveToLast = useCallback(
    (itemId: string) => {
      setItems(prev => {
        const filtered = prev.filter(matchFilter);
        const idx = filtered.findIndex(it => String(it.id) === itemId);
        if (idx === -1 || idx === filtered.length - 1) return prev;
        const reordered = arrayReorder(filtered, idx, filtered.length - 1);
        let fi = 0;
        return prev.map(it => (matchFilter(it) ? reordered[fi++] : it));
      });
      setPreviewItems(null);
    },
    [matchFilter]
  );

  /**
   * Build the save payload. For each project we emit:
   *   { id, display_type, display_order }
   * `display_type` is derived from `project_type` via the same mapping the
   * backend uses (`interiors` / `project-room` / `collaborations`).
   * `display_order` resets to 1 at the start of each category and counts up
   * in the order `items` currently has — so we get 1..N per category.
   * Projects that don't map to any category are skipped.
   */
  const buildPayload = useCallback((): ProjectReorderItem[] => {
    const counters: Record<string, number> = {};
    const payload: ProjectReorderItem[] = [];
    items.forEach(p => {
      const category = getProjectCategory(p.project_type);
      if (!category) return;
      counters[category] = (counters[category] ?? 0) + 1;
      payload.push({
        id: Number(p.id),
        display_type: category,
        display_order: counters[category],
      });
    });
    return payload;
  }, [items]);

  const handleSave = useCallback(async () => {
    const result = await reorderProjects({items: buildPayload()});
    if ('data' in result) {
      dispatch(toast('success', t('common.changesCommited')));
      navigate('/dashboard/projects');
    } else {
      const err: any = (result as any).error;
      const message =
        err?.data?.message || err?.error || t('common.error');
      dispatch(toast('error', message));
    }
  }, [reorderProjects, buildPayload, navigate, dispatch, t]);

  const handleCancel = useCallback(() => {
    navigate('/dashboard/projects');
  }, [navigate]);

  /**
   * Per-category running counter used to display `#1..#N` *inside* a
   * category instead of using the global list index. Computed once per
   * render from the current `items` order.
   */
  const orderByItemId = useMemo(() => {
    const counters: Record<string, number> = {};
    const map: Record<string, number> = {};
    items.forEach(p => {
      const category = getProjectCategory(p.project_type);
      if (!category) return;
      counters[category] = (counters[category] ?? 0) + 1;
      map[String(p.id)] = counters[category];
    });
    return map;
  }, [items]);

  return {
    items,
    loading,
    previewItems,
    setPreviewItems,
    typeFilter,
    setTypeFilter,
    saving,
    onDragEnd,
    moveToFirst,
    moveToLast,
    handleSave,
    handleCancel,
    orderByItemId,
  };
};

export default useProjectsReorder;
